/* Featured Collection Tabs Section */
class FeaturedCollectionTabsSection {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.tabButtons = container.querySelectorAll('.tab-button');
    this.tabPanels = container.querySelectorAll('.tab-panel');
    this.slideshows = new Map();
    this.mobileBreakpoint = 768;
    this.init();
  }

  init() {
    this.setupTabs();
    this.initializeSlideshows();
    this.setupMobileGridSlideshows();
    this.setupResponsiveGridCols();
    this.bindEvents();

    const activePanel = this.container.querySelector('.tab-panel.active');
    if (activePanel) {
      this.initializePanelSlideshow(activePanel);
    }
  }

  setupTabs() {
    this.tabButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.switchTab(button.dataset.tabIndex);
      });
    });
  }

  switchTab(tabIndex) {
    this.tabButtons.forEach(button => {
      button.classList.remove('active');
      if (button.dataset.tabIndex === tabIndex) {
        button.classList.add('active');
      }
    });

    this.tabPanels.forEach(panel => {
      panel.classList.remove('active');
      if (panel.dataset.tabIndex === tabIndex) {
        panel.classList.add('active');
        this.initializePanelSlideshow(panel);
      }
    });
  }

  initializeSlideshows() {
    const swiperContainers = this.container.querySelectorAll('.featured-collection__slideshow');

    swiperContainers.forEach(swiperContainer => {
      const panel = swiperContainer.closest('.tab-panel');
      if (panel) {
        const panelIndex = panel.dataset.tabIndex;
        if (!this.slideshows.has(panelIndex)) {
          this.slideshows.set(panelIndex, {
            container: swiperContainer,
            instance: null
          });
        }
      }
    });
  }

  initializePanelSlideshow(panel) {
    if (!panel || typeof Swiper === 'undefined') return;

    const panelIndex = panel.dataset.tabIndex;
    const slideshowData = this.slideshows.get(panelIndex);

    if (!slideshowData || slideshowData.instance) return;

    const swiperContainer = slideshowData.container;
    const swiperOptions = JSON.parse(swiperContainer.dataset.swiperOptions || '{}');

    const defaultOptions = {
      slidesPerView: 2,
      spaceBetween: 20,
      navigation: {
        nextEl: swiperContainer.querySelector('.featured-collection-slideshow-next'),
        prevEl: swiperContainer.querySelector('.featured-collection-slideshow-prev'),
      },
      pagination: {
        el: ".featured-collection-slideshow-pagination-progressbar",
        type: "progressbar",
      },
      passiveListeners: true,
      breakpoints: {
        0: {
          slidesPerView: 1,
        },
        450: {
          slidesPerView: 2,
        },
        768: {
          slidesPerView: 4,
          spaceBetween: 30,
        }
      },
      autoplay: false,
      loop: false,
      watchSlidesProgress: true,
      a11y: {
        enabled: true,
      },
    };

    const finalOptions = { ...defaultOptions, ...swiperOptions };

    try {
      requestAnimationFrame(() => {
        const swiperInstance = new Swiper(swiperContainer, finalOptions);
        slideshowData.instance = swiperInstance;
      });
    } catch (error) {
    }
  }

  setupMobileGridSlideshows() {
    const gridContainers = this.container.querySelectorAll('.featured-collection--grid.featured-collection--mobile-slideshow .featured-collection__grid');

    gridContainers.forEach(gridContainer => {
      gridContainer.style.scrollSnapType = 'x mandatory';

      const gridItems = gridContainer.querySelectorAll('.featured-collection__grid-item');
      gridItems.forEach(item => {
        item.style.scrollSnapAlign = 'start';
      });
    });
  }

  setupResponsiveGridCols() {
    const gridContainers = this.container.querySelectorAll('.featured-collection__grid');

    gridContainers.forEach(gridContainer => {
      const desktopCols = gridContainer.dataset.desktopCols || 4;
      const mobileCols = gridContainer.dataset.mobileCols || 2;
      gridContainer.style.setProperty('--desktop-cols', desktopCols);
      gridContainer.style.setProperty('--mobile-cols', mobileCols);
    });
  }

  bindEvents() {
    this._handleResize = this.debounce(() => {
      this.handleResize();
    }, 250);
    window.addEventListener('resize', this._handleResize);
  }

  handleResize() {
    this.slideshows.forEach(slideshowData => {
      if (slideshowData.instance) {
        slideshowData.instance.update();
      }
    });
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  destroy() {
    window.removeEventListener('resize', this._handleResize);
    this.slideshows.forEach(slideshowData => {
      if (slideshowData.instance) {
        slideshowData.instance.destroy();
        slideshowData.instance = null;
      }
    });
    this.slideshows.clear();
  }
}

/* Related Products Section Handler */

class RelatedProductsSection {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.recommendationsContainer = container.querySelector(`[id^="product-recommendations-"]`);
    this.swiperInstance = null;

    if (this.recommendationsContainer) {
      this.init();
    }
  }

  init() {
    const url = this.recommendationsContainer.dataset.url;

    if (url && this.recommendationsContainer.querySelector('.loading-placeholder')) {
      this.loadRecommendations(url);
    } else {
      setTimeout(() => {
        this.initializeSwiper();
      }, 100);
    }
  }

  async loadRecommendations(url) {
    try {
      const response = await fetch(url);
      const data = await response.text();

      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = data;

      const newRecommendations = tempDiv.querySelector(`[id^="product-recommendations-"]`);

      if (newRecommendations && newRecommendations.innerHTML.trim()) {
        this.recommendationsContainer.innerHTML = newRecommendations.innerHTML;

        setTimeout(() => {
          this.initializeSwiper();

          if (typeof ProductCardSwatches !== 'undefined') {
            new ProductCardSwatches();
          }

          if (typeof window.initSectionAnimations === 'function') {
            window.initSectionAnimations(`#${this.sectionId}`);
          }
        }, 150);
      } else {
        this.recommendationsContainer.innerHTML = `
          <div style="text-align: center; padding: 20px;">
            <p>No related products available.</p>
          </div>
        `;
      }
    } catch (error) {
      this.recommendationsContainer.innerHTML = `
        <div style="text-align: center; padding: 20px;">
          <p>Unable to load recommendations.</p>
        </div>
      `;
    }
  }

  initializeSwiper() {
    if (typeof Swiper === 'undefined') {
      return;
    }

    const swiperContainer = this.recommendationsContainer.querySelector('.featured-collection__slideshow');

    if (!swiperContainer) {
      return;
    }

    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }

    const desktopProducts = parseInt(this.recommendationsContainer.dataset.desktopProducts) || 4;
    const mobileProducts = parseInt(this.recommendationsContainer.dataset.mobileProducts) || 2;
    const prevBtn = swiperContainer.querySelector('.featured-collection-slideshow-prev');
    const nextBtn = swiperContainer.querySelector('.featured-collection-slideshow-next');
    const paginationEl = swiperContainer.querySelector('.featured-collection-slideshow-pagination-progressbar');

    const swiperOptions = {
      slidesPerView: 1,
      spaceBetween: 20,
      breakpoints: {
        0: {
          slidesPerView: 1,
          spaceBetween: 15
        },
        450: {
          slidesPerView: mobileProducts,
          spaceBetween: 20
        },
        768: {
          slidesPerView: desktopProducts,
          spaceBetween: 20
        }
      },
      loop: false,
      autoplay: false,
      watchSlidesProgress: true,
      a11y: {
        enabled: true,
      },
    };

    if (prevBtn && nextBtn) {
      swiperOptions.navigation = {
        nextEl: nextBtn,
        prevEl: prevBtn
      };
    }

    if (paginationEl) {
      swiperOptions.pagination = {
        el: paginationEl,
        type: 'progressbar'
      };
    }

    try {
      requestAnimationFrame(() => {
        this.swiperInstance = new Swiper(swiperContainer, swiperOptions);
      });
    } catch (error) {
    }
  }

  destroy() {
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
  }
}

/* Initialize all sections on DOM ready */
const featuredCollectionInstances = new Map();

function initFeaturedCollections(container = document) {
  const sections = container.querySelectorAll('.featured-collection-section');

  sections.forEach(section => {
    const id = section.id || 'default';
    if (featuredCollectionInstances.has(id)) {
      featuredCollectionInstances.get(id).destroy();
    }

    let instance = null;
    if (section.querySelector('.tabs-wrapper')) {
      instance = new FeaturedCollectionTabsSection(section);
    } else if (section.querySelector('[id^="product-recommendations-"]')) {
      instance = new RelatedProductsSection(section);
    }

    if (instance) {
      featuredCollectionInstances.set(id, instance);
    }
  });
}

document.addEventListener('DOMContentLoaded', () => initFeaturedCollections());

/* Shopify theme editor support */
if (typeof window.Shopify !== 'undefined' && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('.featured-collection-section')) {
      initFeaturedCollections(event.target);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('.featured-collection-section');
    if (section && section.id && featuredCollectionInstances.has(section.id)) {
      featuredCollectionInstances.get(section.id).destroy();
      featuredCollectionInstances.delete(section.id);
    }
  });

  document.addEventListener('shopify:section:select', (event) => {
    const section = event.target.querySelector('.featured-collection-section');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });

  document.addEventListener('shopify:block:select', (event) => {
    const blockId = event.detail.blockId;
    const section = event.target.closest('.featured-collection-section');

    if (section && section.id) {
      const instance = featuredCollectionInstances.get(section.id);
      if (instance instanceof FeaturedCollectionTabsSection) {
        const tabButton = section.querySelector(`[data-block-id="${blockId}"]`);
        if (tabButton && tabButton.dataset.tabIndex) {
          instance.switchTab(tabButton.dataset.tabIndex);
        }
      }
    }
  });
}

/* Export for module usage */
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { FeaturedCollectionTabsSection, RelatedProductsSection };
}

/* Sale Slider Scrolling Handler */
class ScrollingHandler extends HTMLElement {
  constructor() {
    super();
    this.width = 0;
    this.scrolling = this.querySelector('.sale-scrolling-inner');

    setTimeout(() => {
      this.run();
    }, 100);

    this._boundRun = this.run.bind(this);
    ['resize', 'shopify:section:load', 'shopify:block:select'].forEach(evt => window.addEventListener(evt, this._boundRun));
  }

  disconnectedCallback() {
    ['resize', 'shopify:section:load', 'shopify:block:select'].forEach(evt => window.removeEventListener(evt, this._boundRun));
  }

  run() {
    if (!this.scrolling) return;

    requestAnimationFrame(() => {
      const wrap = this.closest('.sale-scrolling-wrapper');
      if (!wrap) return;

      const containerWidth = wrap.offsetWidth;
      const textWidth = this.scrolling.scrollWidth;

      if (textWidth === 0) {
        setTimeout(() => this.run(), 100);
        return;
      }

      requestAnimationFrame(() => {
        this.scrolling.classList.remove('sale-scrolling-animation');
        this.querySelectorAll('.sale-scrolling-dup').forEach(e => e.remove());

        const need = Math.ceil(containerWidth / textWidth) + 2;

        const fragment = document.createDocumentFragment();
        for (let i = 0; i < need; i++) {
          const clone = this.scrolling.cloneNode(true);
          clone.classList.add('sale-scrolling-dup', 'sale-scrolling-animation');
          fragment.appendChild(clone);
        }
        this.appendChild(fragment);

        requestAnimationFrame(() => {
          this.scrolling.classList.add('sale-scrolling-animation');
        });
      });
    });
  }
}

if (!customElements.get("scrolling-item")) {
  customElements.define("scrolling-item", ScrollingHandler);
}

/* Product Card Variant Swatches & Add to Cart */
class ProductCardSwatches {
  static initialized = false;

  constructor() {
    this.init();
  }

  init() {
    if (!ProductCardSwatches.initialized) {
      this.bindSwatchEvents();
      this.bindAddToCartEvents();
      ProductCardSwatches.initialized = true;
    }
  }

  bindSwatchEvents() {
    document.addEventListener('click', (e) => {
      const swatchItem = e.target.closest('.card-swatch-item');
      if (swatchItem) {
        e.preventDefault();
        e.stopPropagation();
        this.handleSwatchClick(swatchItem);
      }
    });
  }

  handleSwatchClick(swatchItem) {
    const productId = swatchItem.dataset.productId;
    const productCard = swatchItem.closest('.card-wrapper');
    if (!productCard) return;

    const optionGroup = swatchItem.closest('.card-variant-swatches');
    if (optionGroup) {
      const groupSwatches = optionGroup.querySelectorAll('.card-swatch-item');
      groupSwatches.forEach(swatch => {
        swatch.classList.remove('active');
      });
      swatchItem.classList.add('active');
    }

    const selectedOptions = this.getSelectedOptions(productCard);
    const variantsData = this.getProductVariantsData(productCard, productId);
    if (!variantsData || !variantsData.variants) return;

    const matchingVariant = this.findMatchingVariant(variantsData.variants, selectedOptions);

    if (matchingVariant) {
      const variantImage = matchingVariant.featured_image?.src;
      if (variantImage) {
        const mainImage = productCard.querySelector('.card-main-image');
        if (mainImage) {
          mainImage.style.opacity = '0.5';
          setTimeout(() => {
            mainImage.src = variantImage;
            mainImage.srcset = variantImage;
            mainImage.style.opacity = '1';
          }, 150);
        }
      }

      const variantInput = productCard.querySelector('.variant-id-input');
      if (variantInput) {
        variantInput.value = matchingVariant.id;
        variantInput.dataset.variantId = matchingVariant.id;
        variantInput.dataset.requiresSellingPlan = matchingVariant.requires_selling_plan ? 'true' : 'false';
      }

      const addToCartBtn = productCard.querySelector('.add-to-cart');
      if (addToCartBtn) {
        if (matchingVariant.available) {
          addToCartBtn.disabled = false;
          addToCartBtn.classList.remove('disabled');
        } else {
          addToCartBtn.disabled = true;
          addToCartBtn.classList.add('disabled');
        }
      }

      this.updatePrice(productCard, matchingVariant);
      this.updateOptionAvailability(productCard, variantsData.variants, selectedOptions);
    }
  }

  getSelectedOptions(productCard) {
    const selectedOptions = {};
    const optionGroups = productCard.querySelectorAll('.card-variant-swatches');

    optionGroups.forEach(group => {
      const activeButton = group.querySelector('.card-swatch-item.active');
      if (activeButton) {
        const position = parseInt(activeButton.dataset.optionPosition);
        const value = activeButton.dataset.optionValue;
        selectedOptions[`option${position}`] = value;
      }
    });

    return selectedOptions;
  }

  getProductVariantsData(productCard, productId) {
    const dataScript = productCard.querySelector(`.product-variants-data[data-product-id="${productId}"]`);
    if (!dataScript) return null;

    try {
      return JSON.parse(dataScript.textContent);
    } catch (e) {
      return null;
    }
  }

  findMatchingVariant(variants, selectedOptions) {
    const matchingVariant = variants.find(variant => {
      return Object.keys(selectedOptions).every(optionKey => {
        return variant[optionKey] === selectedOptions[optionKey];
      });
    });
    return matchingVariant || null;
  }

  updatePrice(productCard, variant) {
    const priceWrapper = productCard.querySelector('.card-price-wrapper');
    if (!priceWrapper) return;

    const priceContainer = priceWrapper.querySelector('.price');
    if (!priceContainer) return;

    const isOnSale = variant.compare_at_price && variant.compare_at_price > variant.price;
    const isSoldOut = !variant.available;
    const moneyFormat = window.themeVariables?.settings?.currencyCodeEnabled
      ? window.themeVariables?.settings?.moneyWithCurrencyFormat
      : window.themeVariables?.settings?.moneyFormat;

    priceContainer.classList.toggle('price--on-sale', isOnSale);
    priceContainer.classList.toggle('price--sold-out', isSoldOut);

    const currentPriceEls = priceWrapper.querySelectorAll('[data-current-price]');
    currentPriceEls.forEach(el => {
      el.innerHTML = window.formatMoney ? window.formatMoney(variant.price, moneyFormat) : (variant.price / 100).toFixed(2);
    });

    const comparePriceValueEls = priceWrapper.querySelectorAll('[data-compare-price-value]');
    comparePriceValueEls.forEach(el => {
      if (variant.compare_at_price) {
        el.innerHTML = window.formatMoney ? window.formatMoney(variant.compare_at_price, moneyFormat) : (variant.compare_at_price / 100).toFixed(2);
      } else {
        el.innerHTML = '';
      }
    });

    const regularPriceBlock = priceWrapper.querySelector('[data-regular-price]');
    const salePriceBlock = priceWrapper.querySelector('[data-sale-price]');
    const compareWrapper = priceWrapper.querySelector('.price-compare-wrapper');

    if (regularPriceBlock) regularPriceBlock.style.display = isOnSale ? 'none' : '';
    if (salePriceBlock) {
      salePriceBlock.style.display = isOnSale ? '' : 'none';
      if (compareWrapper) compareWrapper.style.display = isOnSale ? '' : 'none';
    }

    const priceBadges = priceWrapper.querySelectorAll('[data-price-badge]');
    priceBadges.forEach(badgeContainer => {
      const soldOutBadge = badgeContainer.querySelector('[data-badge-type="sold-out"]');
      const saleBadge = badgeContainer.querySelector('[data-badge-type="sale"]');

      if (soldOutBadge) soldOutBadge.style.display = isSoldOut ? 'inline-block' : 'none';
      if (saleBadge) saleBadge.style.display = (!isSoldOut && isOnSale) ? 'inline-block' : 'none';

      badgeContainer.style.display = (isSoldOut || isOnSale) ? 'block' : 'none';
    });
  }

  updateOptionAvailability(productCard, variants, selectedOptions) {
    const optionGroups = productCard.querySelectorAll('.card-variant-swatches');

    optionGroups.forEach(group => {
      const optionPosition = parseInt(group.dataset.optionPosition);
      const optionKey = `option${optionPosition}`;
      const buttons = group.querySelectorAll('.card-swatch-item');

      buttons.forEach(button => {
        const optionValue = button.dataset.optionValue;
        const testOptions = { ...selectedOptions };
        testOptions[optionKey] = optionValue;

        const availableVariant = variants.find(variant => {
          return Object.keys(testOptions).every(key => {
            return variant[key] === testOptions[key];
          }) && variant.available;
        });

        if (availableVariant) {
          button.dataset.variantAvailable = 'true';
          button.classList.remove('unavailable');
        } else {
          button.dataset.variantAvailable = 'false';
          button.classList.add('unavailable');
        }
      });
    });
  }

  bindAddToCartEvents() {
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('.product-card-form');
      if (form) {
        e.preventDefault();
        this.handleAddToCart(form);
      }
    });
  }

  async handleAddToCart(form) {
    const addToCartBtn = form.querySelector('[data-add-to-cart]');
    if (!addToCartBtn || addToCartBtn.disabled) return;

    const variantIdInput = form.querySelector('[name="id"]');
    if (variantIdInput && variantIdInput.dataset.requiresSellingPlan === 'true') {
      window.location.href = form.closest('.card-wrapper').querySelector('.card-heading a').href;
      return;
    }

    const originalHTML = addToCartBtn.innerHTML;
    addToCartBtn.disabled = true;
    addToCartBtn.innerHTML = `ADDING...`;

    try {
      const variantId = form.querySelector('[name="id"]').value;

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Failed to add item to cart');
      }

      this.showAddedFeedback(addToCartBtn, originalHTML);
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      this.updateCartCount(cart.item_count);

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));

      const cartType = document.body.dataset.cartType || 'drawer';
      if (cartType === 'drawer') {
        this.openCartDrawer();
      } else if (cartType === 'page') {
        window.location.href = '/cart';
      }
    } catch (error) {
      alert(error.message);
      addToCartBtn.disabled = false;
      addToCartBtn.innerHTML = originalHTML;
    }
  }

  showAddedFeedback(button, originalHTML) {
    button.innerHTML = `ADDED`;
    setTimeout(() => {
      button.disabled = false;
      button.innerHTML = originalHTML;
    }, 2000);
  }

  openCartDrawer() {
    const drawerElement = document.querySelector('.cart-drawer-wrap');
    if (drawerElement) {
      drawerElement.classList.add('active');
      document.body.classList.add('drawer-open');
    }
  }

  updateCartCount(count) {
    const cartCountElements = document.querySelectorAll('[data-cart-count]');
    cartCountElements.forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new ProductCardSwatches();
});